'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { processHeading } from '@workspace/lib/heading-utils';
import { getBackgroundClass } from '@workspace/lib/helper';
import type { UseGalleryHookResult, UseGalleryParams } from './gallery.types';
import { SanityImage } from '@workspace/interfaces';

type GalleryImageValue = (SanityImage & Record<string, any>) | Record<string, any> | string | null | undefined;

const isRecord = (value: unknown): value is Record<string, any> => typeof value === 'object' && value !== null;

const getBynderUrl = (value: GalleryImageValue): string => {
  if (!isRecord(value)) return '';
  return value.selectedUrl || value.url || '';
};

const hasRenderableImageSource = (value: GalleryImageValue): boolean => {
  if (typeof value === 'string') return Boolean(value);
  if (!isRecord(value)) return false;

  return Boolean(
    value.asset?._ref ||
    value.asset?._id ||
    value.asset?.url ||
    value.url ||
    value.selectedUrl ||
    value.bynderImage?.selectedUrl ||
    value.bynderImage?.url,
  );
};

const bynderToImage = (bynderImage: GalleryImageValue, fallback: Record<string, any>): SanityImage | null => {
  const url = getBynderUrl(bynderImage);
  if (!url || !isRecord(bynderImage)) return null;

  return {
    _key: fallback._key,
    _type: 'image',
    asset: {
      _id: bynderImage.id || bynderImage.databaseId || url,
      _ref: '',
      _type: 'reference',
      url,
    },
    alt: bynderImage.alt || bynderImage.description || bynderImage.name || fallback.alt,
    title: bynderImage.name || fallback.title,
    url,
    width: bynderImage.width,
    height: bynderImage.height,
    bynderImage,
  } as SanityImage;
};

export const normalizeGalleryImage = (item: GalleryImageValue): SanityImage | null => {
  if (!isRecord(item)) {
    return typeof item === 'string'
      ? ({ asset: { _id: item, _ref: '', _type: 'reference', url: item }, url: item } as SanityImage)
      : null;
  }

  if (hasRenderableImageSource(item.src) && isRecord(item.src) && item.src.bynderImage) {
    return { ...item.src, _key: item.src._key || item._key, asset: item.src.asset } as SanityImage;
  }

  const bynderImage = item.bynderSrc || item.bynderImage;
  const normalizedBynderImage = bynderToImage(bynderImage, item);
  if (normalizedBynderImage) return normalizedBynderImage;

  const source = item.src || item.image || item.thumbnail || item;
  if (!hasRenderableImageSource(source)) return null;

  if (isRecord(source)) {
    return {
      ...source,
      _key: source._key || item._key,
      asset: source.asset,
      alt: source.alt || item.alt,
      title: source.title || item.title,
    } as SanityImage;
  }

  return {
    _key: item._key,
    asset: { _id: source, _ref: '', _type: 'reference', url: source },
    url: source,
    alt: item.alt,
  } as SanityImage;
};

export const useGallery = ({
  images,
  isOpen,
  startIndex = 0,
  onRequestClose,
  heading,
  backgroundColor,
}: UseGalleryParams & {
  heading?: any;
  backgroundColor?: any;
}): UseGalleryHookResult => {
  const [isGalleryOpen, setIsGalleryOpen] = useState<boolean>(!!isOpen);
  const [selectedIndex, setSelectedIndex] = useState<number>(startIndex);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

  const gallerySlides: SanityImage[] = useMemo(() => {
    if (!images) return [];
    return images.map(normalizeGalleryImage).filter((img): img is SanityImage => Boolean(img));
  }, [images]);

  const [{ text: headingText }] = useMemo(() => [processHeading(heading)], [heading]);
  const backgroundClass = useMemo(() => getBackgroundClass(backgroundColor), [backgroundColor]);

  // Embla carousel for infinite thumbnail scrolling
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  const handleImageClick = useCallback((index: number) => {
    setSelectedIndex(index);
    setIsGalleryOpen(true);
  }, []);

  const updateScrollButtons = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollLeft(emblaApi.canScrollPrev());
    setCanScrollRight(emblaApi.canScrollNext());
  }, [emblaApi]);

  const scrollThumbnails = useCallback(
    (direction: 'left' | 'right') => {
      if (!emblaApi) return;
      if (direction === 'left') {
        emblaApi.scrollPrev();
      } else {
        emblaApi.scrollNext();
      }
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    updateScrollButtons();
    emblaApi.on('select', updateScrollButtons);
    emblaApi.on('reInit', updateScrollButtons);
    return () => {
      emblaApi.off('select', updateScrollButtons);
      emblaApi.off('reInit', updateScrollButtons);
    };
  }, [emblaApi, updateScrollButtons]);

  const getVisibleThumbnailIndices = useCallback((length: number, selected: number, windowSize = 3): number[] => {
    if (length <= windowSize) return Array.from({ length }, (_, i) => i);
    let start = selected - Math.floor(windowSize / 2);
    if (start < 0) start = 0;
    if (start > length - windowSize) start = length - windowSize;
    return Array.from({ length: windowSize }, (_, k) => start + k);
  }, []);

  const visibleThumbIndices = useMemo(() => {
    return getVisibleThumbnailIndices(gallerySlides.length, selectedIndex, 3);
  }, [gallerySlides.length, selectedIndex, getVisibleThumbnailIndices]);

  const onSlide = useCallback((i: number) => setSelectedIndex(i), []);

  const closeGallery = useCallback(() => {
    setIsGalleryOpen(false);
    onRequestClose && onRequestClose();
  }, [onRequestClose]);

  // Combined effect for external state updates to reduce effect count
  useEffect(() => {
    if (typeof isOpen === 'boolean') {
      setIsGalleryOpen(isOpen);
    }
    setSelectedIndex(startIndex);
  }, [isOpen, startIndex]);

  return {
    isGalleryOpen,
    selectedIndex,
    canScrollLeft,
    canScrollRight,
    gallerySlides,
    emblaRef,
    visibleThumbIndices,
    handleImageClick,
    scrollThumbnails,
    onSlide,
    closeGallery,
    images: gallerySlides,
    headingText,
    backgroundClass,
  };
};
